import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, ShoppingCart } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext } from 'ra-core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useHasPermission } from '../rbac/useHasPermission';
import type { BoM, Vendor } from '../types';

export const GeneratePOButton = () => {
    const record = useRecordContext<BoM>();
    const [open, setOpen] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState<string>('');
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [fetchingVendors, setFetchingVendors] = useState(false);

    const notify = useNotify();
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const { hasPermission: canManage } = useHasPermission('manage_production');

    if (!record || record.status !== 'approved' || !canManage) return null;

    const handleOpen = async () => {
        setOpen(true);
        setFetchingVendors(true);
        try {
            const { data } = await dataProvider.getList('vendors', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: { status: 'approved' }
            });
            setVendors(data);
        } catch (error) {
            notify('Error fetching vendors', { type: 'error' });
        } finally {
            setFetchingVendors(false);
        }
    };

    const handleConfirm = () => {
        if (!selectedVendor) return;
        setOpen(false);

        // Route through the standard PO create form (same as RFQ→PO) so this
        // draft gets a real sequential reference, the CGST/SGST/IGST split from
        // GrandTotalsWatcher, and the unapproved-vendor threshold check in
        // POCreate's onSuccess — instead of duplicating (and drifting from)
        // that logic here.
        navigate('/purchase_orders/create', {
            state: {
                record: {
                    vendor_id:  selectedVendor,
                    project_id: record.project_id,
                    status:     'draft',
                    items: record.items.map(bomItem => ({
                        description: (bomItem as any).description || 'Unknown Item',
                        hsn_code:    (bomItem as any).hsn_code || '',
                        quantity:    bomItem.quantity,
                        unit:        bomItem.unit || 'Nos',
                        unit_price:  bomItem.estimated_unit_cost || 0,
                    })),
                },
            },
        });
    };

    return (
        <>
            <Button 
                onClick={handleOpen}
                variant="outline"
                className="border-green-200 bg-green-50 text-green-700 hover:bg-green-100 gap-2 shadow-sm"
            >
                <ShoppingCart className="w-4 h-4" />
                Generate PO
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-blue-600" />
                            Generate Purchase Order
                        </DialogTitle>
                        <DialogDescription>
                            Create a draft PO based on this approved Bill of Materials.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="py-4 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="vendor">Select Target Vendor</Label>
                            <Select onValueChange={setSelectedVendor} disabled={fetchingVendors}>
                                <SelectTrigger>
                                    <SelectValue placeholder={fetchingVendors ? "Loading vendors..." : "Choose a vendor"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {vendors.map(vendor => (
                                        <SelectItem key={vendor.id} value={vendor.id.toString()}>
                                            {vendor.name}
                                        </SelectItem>
                                    ))}
                                    {vendors.length === 0 && !fetchingVendors && (
                                        <div className="p-2 text-xs text-muted-foreground flex items-center gap-2">
                                            <AlertCircle className="w-3 h-3" /> No approved vendors found.
                                        </div>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
                            <p className="text-[11px] text-blue-700 leading-relaxed">
                                <strong>Note:</strong> This will pull all {record.items?.length || 0} items from the BoM into a new draft Purchase Order. You can review and adjust prices before sending.
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={!selectedVendor}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Create Draft PO
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
